import { push } from 'connected-react-router';
import { ToastType } from 'react-toastify';
import { Severity } from '@sentry/types';
import { captureMessage } from '@sentry/browser';
import { openAssociationModal } from '@domain/settings/account-association-modal/account-association-modal.state';
import { showSuccessToast } from '@shared/toasts/toasts.state';
import { showToast } from '@shared/components/messages-and-errors/ToastsNotifications';
import { createMiddleware } from '@shared/redux';
import { ROUTES, TEXTS } from '../../integrations.constants';
import {
    REQUEST_REDIRECT_TO_SSO,
    REQUEST_REDIRECT_TO_INTEGRATIONS_HOME,
    START_INTEGRATION_FLOW,
    SEND_AUTHORIZATION_CODE_REQUEST_FAIL,
    SEND_AUTHORIZATION_CODE_REQUEST_SUCCESS,
    THROW_INTEGRATION_FLOW_ERROR,
    THROW_INTEGRATION_FLOW_WARNING,
    requestRedirectToIntegrationsHome,
    sendAuthorizationCode,
    throwIntegrationFlowError,
    throwIntegrationFlowWarning,
} from './oauth-strategy.state';

import {
    ASSOCIATE_ACCOUNTS_PLATFORMS,
    ERROR_AUTHORIZATION_CODE, ERROR_STATE_INVALID, MESSAGES,
} from './oauth-strategy.constants';
import {
    getHomePathForIntegrationId,
    isStateCodeValid,
    shouldThrowWarning,
} from './oauth-strategy-utils';

export const getRequestedActionProviderId = action => action.requestAction.payload.providerId;

export const oauthStrategyMiddleware = createMiddleware({
    [REQUEST_REDIRECT_TO_SSO]: (_, action) => {
        window.location.href = action.payload.url;
    },
    [START_INTEGRATION_FLOW]: (_, action) => {
        const {
            authorizationServerUrl,
            providerId,
            state,
            params,
        } = action.payload;

        return isStateCodeValid(providerId, state)
            ? sendAuthorizationCode({ params, authorizationServerUrl, providerId })
            : throwIntegrationFlowError(ERROR_STATE_INVALID, 'INVALID_STATE');
    },
    // eslint-disable-next-line complexity
    [SEND_AUTHORIZATION_CODE_REQUEST_FAIL]: (_, action) => {
        const message: string = Object.values(
            action?.payload?.response?.data?.errors || { message: ERROR_AUTHORIZATION_CODE },
        )[0];
        if (shouldThrowWarning(message)) {
            return throwIntegrationFlowWarning(MESSAGES[message], 'REQUEST_AUTHORIZATION');
        }

        const isSuccessfulResponse = (action?.payload?.response?.code || 500) < 400;

        try {
            const errorMessage = JSON.stringify(action);
            // eslint-disable-next-line no-console
            console.log(SEND_AUTHORIZATION_CODE_REQUEST_FAIL, { errorMessage });
            captureMessage(errorMessage, isSuccessfulResponse ? Severity.Fatal : Severity.Error);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(SEND_AUTHORIZATION_CODE_REQUEST_FAIL, { action, error });
        }
        if (!isSuccessfulResponse) return throwIntegrationFlowError(message, 'REQUEST_AUTHORIZATION');
        return [];
    },
    [THROW_INTEGRATION_FLOW_ERROR]: (_, action) => {
        showToast(action.payload.message, ToastType.ERROR, {
            autoClose: 60000,
            toastId: action.payload.phase,
            height: 300,
        });

        return requestRedirectToIntegrationsHome();
    },
    [THROW_INTEGRATION_FLOW_WARNING]: (_, action) => {
        showToast(action.payload.message, ToastType.WARNING, {
            autoClose: 30000,
            toastId: action.payload.phase,
        });

        return requestRedirectToIntegrationsHome();
    },
    [REQUEST_REDIRECT_TO_INTEGRATIONS_HOME]: (_, action) => {
        if (action.payload) { return push(getHomePathForIntegrationId(action.payload)); }
        return push(ROUTES.LIST);
    },
    [SEND_AUTHORIZATION_CODE_REQUEST_SUCCESS]: (_, action) => {
        const platformIntegrated = getRequestedActionProviderId(action);

        const shouldOpenModal = Object.values(ASSOCIATE_ACCOUNTS_PLATFORMS)
            .includes(platformIntegrated);

        if (shouldOpenModal) {
            return [
                showSuccessToast({ message: TEXTS.OPEN_ASSOCIATE_ACCOUNTS_MODAL }),
                requestRedirectToIntegrationsHome(platformIntegrated),
                openAssociationModal(),
            ];
        }
        return [requestRedirectToIntegrationsHome()];
    },
});
